import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CR_API_ENDPOINTS } from '../constants';
import { apiService } from '../services';
import { ImportStep, LenderPackageImportResult, PackagePreview } from '../types';

interface UseLenderPackageImportReturn {
  step: ImportStep;
  selectedFile: File | null;
  packages: PackagePreview[];
  selectedKeys: Set<string>;
  isLoading: boolean;
  result: LenderPackageImportResult | null;
  isReplaceWarningOpen: boolean;
  /** Selected packages that already exist and would be replaced on import. */
  selectedExistingPackages: PackagePreview[];
  setSelectedFile: (file: File | null) => void;
  previewPackages: (lenderId: string) => Promise<void>;
  toggleKey: (key: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  /** Begin import: opens the replace warning if any selected package exists, otherwise imports directly. */
  requestImport: (lenderId: string) => void;
  /** Confirm the replace warning and run the import. */
  confirmImport: (lenderId: string) => Promise<void>;
  closeReplaceWarning: () => void;
  goToUpload: () => void;
  reset: () => void;
}

/**
 * Encapsulates the lender-package CSV import wizard: upload → preview/select → import.
 * Flags packages that already exist and gates the import behind a replace confirmation.
 * @returns Import wizard state and handlers shared by all import entry points
 */
export const useLenderPackageImport = (): UseLenderPackageImportReturn => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [packages, setPackages] = useState<PackagePreview[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<LenderPackageImportResult | null>(null);
  const [isReplaceWarningOpen, setIsReplaceWarningOpen] = useState(false);

  const selectedExistingPackages = useMemo(
    () => packages.filter((pkg) => pkg.already_exists && selectedKeys.has(pkg.package_key)),
    [packages, selectedKeys],
  );

  const previewPackages = useCallback(async (lenderId: string): Promise<void> => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const response = await apiService.uploadFile<{ packages: PackagePreview[] }>(
        `${CR_API_ENDPOINTS.LENDER_PACKAGES_PREVIEW}/${lenderId}`,
        selectedFile,
      );
      setPackages(response.packages);
      setSelectedKeys(new Set(response.packages.map((pkg) => pkg.package_key)));
      setStep('select');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Preview failed');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile]);

  const toggleKey = useCallback((key: string): void => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((): void => {
    setSelectedKeys(new Set(packages.map((pkg) => pkg.package_key)));
  }, [packages]);

  const deselectAll = useCallback((): void => {
    setSelectedKeys(new Set());
  }, []);

  const runImport = useCallback(async (lenderId: string): Promise<void> => {
    if (!selectedFile || selectedKeys.size === 0) return;

    setIsLoading(true);
    try {
      const importResult = await apiService.uploadFileWithFields<LenderPackageImportResult>(
        `${CR_API_ENDPOINTS.LENDER_PACKAGES}/import/${lenderId}`,
        selectedFile,
        { selected_keys: JSON.stringify(Array.from(selectedKeys)) },
      );
      setResult(importResult);
      setStep('done');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import failed');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, selectedKeys]);

  const requestImport = useCallback((lenderId: string): void => {
    if (selectedKeys.size === 0) return;
    if (selectedExistingPackages.length > 0) {
      setIsReplaceWarningOpen(true);
      return;
    }
    void runImport(lenderId);
  }, [selectedKeys, selectedExistingPackages, runImport]);

  const confirmImport = useCallback(async (lenderId: string): Promise<void> => {
    setIsReplaceWarningOpen(false);
    await runImport(lenderId);
  }, [runImport]);

  const closeReplaceWarning = useCallback((): void => {
    setIsReplaceWarningOpen(false);
  }, []);

  const goToUpload = useCallback((): void => {
    setStep('upload');
  }, []);

  const reset = useCallback((): void => {
    setStep('upload');
    setSelectedFile(null);
    setPackages([]);
    setSelectedKeys(new Set());
    setResult(null);
    setIsReplaceWarningOpen(false);
  }, []);

  return {
    step,
    selectedFile,
    packages,
    selectedKeys,
    isLoading,
    result,
    isReplaceWarningOpen,
    selectedExistingPackages,
    setSelectedFile,
    previewPackages,
    toggleKey,
    selectAll,
    deselectAll,
    requestImport,
    confirmImport,
    closeReplaceWarning,
    goToUpload,
    reset,
  };
};
